import { useEffect, useLayoutEffect } from 'react';

const STORAGE_KEY = 'sshop:reload-scroll';

type SavedScroll = { url: string; y: number };

function currentUrl() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/** Wait for async page content before restoring a hard refresh's scroll position. */
export function ReloadScrollRestoration() {
  useEffect(() => {
    const save = () => {
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ url: currentUrl(), y: window.scrollY } satisfies SavedScroll),
        );
      } catch {
        // Storage may be disabled; normal navigation still works without it.
      }
    };

    window.addEventListener('pagehide', save);
    return () => window.removeEventListener('pagehide', save);
  }, []);

  useLayoutEffect(() => {
    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (navigation?.type !== 'reload') return;

    let saved: SavedScroll | null = null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      saved = raw ? (JSON.parse(raw) as SavedScroll) : null;
    } catch {
      return;
    }
    if (saved?.url !== currentUrl() || !Number.isFinite(saved.y) || saved.y <= 0) return;

    const targetY = saved.y;
    const root = document.documentElement;
    const previousAnchor = root.style.getPropertyValue('overflow-anchor');
    const previousPriority = root.style.getPropertyPriority('overflow-anchor');
    root.style.setProperty('overflow-anchor', 'none');

    let finished = false;
    let restored = false;
    let settleTimer = 0;
    const observer = new ResizeObserver(restoreWhenReady);
    const finish = () => {
      if (finished) return;
      finished = true;
      observer.disconnect();
      window.removeEventListener('load', restoreWhenReady);
      window.removeEventListener('wheel', finish);
      window.removeEventListener('touchstart', finish);
      window.removeEventListener('pointerdown', finish);
      window.removeEventListener('keydown', finish);
      window.clearTimeout(settleTimer);
      window.clearTimeout(timeout);
      if (previousAnchor) root.style.setProperty('overflow-anchor', previousAnchor, previousPriority);
      else root.style.removeProperty('overflow-anchor');
    };
    function restoreWhenReady() {
      if (finished || root.scrollHeight - window.innerHeight < targetY - 2) return;
      window.scrollTo({ top: targetY, behavior: 'instant' });
      restored = true;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        window.scrollTo({ top: targetY, behavior: 'instant' });
        finish();
      }, 1000);
    }

    observer.observe(document.body);
    window.addEventListener('load', restoreWhenReady);
    window.addEventListener('wheel', finish, { passive: true });
    window.addEventListener('touchstart', finish, { passive: true });
    window.addEventListener('pointerdown', finish);
    window.addEventListener('keydown', finish);
    const timeout = window.setTimeout(() => {
      if (restored) window.scrollTo({ top: targetY, behavior: 'instant' });
      finish();
    }, 10000);
    restoreWhenReady();
    return finish;
  }, []);

  return null;
}
